// src/components/technician/TechnicianSidebar.jsx
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  IconButton, Box, Tooltip, Typography, Fab, alpha,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import DashboardIcon from "@mui/icons-material/SpaceDashboard";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import HelpIcon from "@mui/icons-material/HelpOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import LogoutConfirmation from "../../components/Modals/LogoutConfirmation";

const SIDEBAR_WIDTH_EXPANDED = 240;
const SIDEBAR_WIDTH_COLLAPSED = 76;
const BRAND_PRIMARY = "#0f766e"; // teal — visually distinct from admin blue
const BRAND_LIGHT = "#f0fdfa";

const NAV_SECTIONS = [
  {
    title: "Main",
    items: [
      { path: "/technician/dashboard", key: "dashboard", label: "Dashboard", icon: <DashboardIcon /> },
      { path: "/technician/tickets",   key: "tickets",   label: "My Tickets", icon: <ConfirmationNumberIcon /> },
    ],
  },
  {
    title: "Support",
    items: [
      { path: "/technician/help",     key: "help",     label: "Help",     icon: <HelpIcon /> },
      { path: "/technician/settings", key: "settings", label: "Settings", icon: <SettingsIcon /> },
    ],
  },
];

const TechnicianSidebar = ({ logout, isOpen, isMobile, onSidebarToggle, currentUser }) => {
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const currentWidth = isOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;
  const handleToggle = () => onSidebarToggle(!isOpen);
  const handleNavClick = () => isMobile && onSidebarToggle(false);

  const getActiveKey = () => {
    const all = NAV_SECTIONS.flatMap((s) => s.items);
    all.sort((a, b) => b.path.length - a.path.length);
    const m = all.find((i) => location.pathname.startsWith(i.path));
    return m ? m.key : "";
  };
  const activeKey = getActiveKey();

  const handleLogoutConfirm = async () => {
    setLogoutLoading(true);
    try { await logout(); }
    finally { setLogoutLoading(false); setShowLogoutModal(false); }
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", background: "linear-gradient(180deg,#ffffff 0%,#fafbfc 100%)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: isOpen ? "space-between" : "center",
                 px: isOpen ? 1.5 : 1, py: 1.5, minHeight: 72, flexShrink: 0,
                 borderBottom: "1px solid", borderColor: alpha("#000", 0.06) }}>
        {isOpen ? (
          <>
            <Box component={Link} to="/technician/dashboard"
                 sx={{ flex: 1, minWidth: 0, height: 48, display: "flex", alignItems: "center", textDecoration: "none" }}>
              <Box component="img" src="/logo.png" alt="Logo"
                   onError={(e) => { e.target.onerror = null; e.target.src = "/default-logo.png"; }}
                   sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            </Box>
            <IconButton onClick={handleToggle} size="small"
                        sx={{ color: "text.secondary", "&:hover": { color: BRAND_PRIMARY, bgcolor: alpha(BRAND_PRIMARY, 0.08) } }}>
              <MenuOpenIcon />
            </IconButton>
          </>
        ) : (
          <IconButton onClick={handleToggle}
                      sx={{ color: BRAND_PRIMARY, bgcolor: BRAND_LIGHT, "&:hover": { bgcolor: alpha(BRAND_PRIMARY, 0.15) } }}>
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      <Box component="nav" sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 1, px: 1.25 }}>
        {NAV_SECTIONS.map((section, idx) => (
          <Box key={section.title} sx={{ mb: idx === NAV_SECTIONS.length - 1 ? 0 : 1.25 }}>
            {isOpen && (
              <Typography variant="caption"
                          sx={{ px: 2, py: 0.5, display: "block", fontSize: "0.7rem",
                                fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                                color: "text.secondary", opacity: 0.7 }}>
                {section.title}
              </Typography>
            )}
            <List disablePadding>
              {section.items.map(({ path, key, label, icon }) => {
                const isActive = activeKey === key;
                return (
                  <ListItem key={key} disablePadding sx={{ mb: 0.25 }}>
                    <Tooltip title={!isOpen ? label : ""} placement="right" arrow>
                      <ListItemButton component={Link} to={path} onClick={handleNavClick}
                        sx={{
                          minHeight: 44, borderRadius: "10px",
                          justifyContent: isOpen ? "flex-start" : "center",
                          px: isOpen ? 1.5 : 0, mx: isOpen ? 0 : "auto",
                          width: isOpen ? "100%" : 48,
                          bgcolor: isActive ? BRAND_LIGHT : "transparent",
                          color: isActive ? BRAND_PRIMARY : "text.primary",
                          "&:hover": { bgcolor: isActive ? BRAND_LIGHT : alpha("#000", 0.04) },
                        }}>
                        <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 1.75 : 0, justifyContent: "center",
                                            color: isActive ? BRAND_PRIMARY : "text.secondary",
                                            "& svg": { fontSize: "1.35rem" } }}>
                          {icon}
                        </ListItemIcon>
                        {isOpen && (
                          <ListItemText primary={label}
                                        primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: isActive ? 600 : 500, noWrap: true }} />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box sx={{ p: 1.25, flexShrink: 0, borderTop: "1px solid", borderColor: alpha("#000", 0.06) }}>
        <Tooltip title={!isOpen ? "Logout" : ""} placement="right" arrow>
          <ListItemButton onClick={() => setShowLogoutModal(true)}
            sx={{ minHeight: 44, borderRadius: "10px",
                  justifyContent: isOpen ? "flex-start" : "center",
                  px: isOpen ? 1.5 : 0, mx: isOpen ? 0 : "auto",
                  width: isOpen ? "100%" : 48, color: "#dc2626",
                  "&:hover": { bgcolor: alpha("#dc2626", 0.08) } }}>
            <ListItemIcon sx={{ minWidth: 0, mr: isOpen ? 1.75 : 0, justifyContent: "center",
                                color: "#dc2626", "& svg": { fontSize: "1.35rem" } }}>
              <LogoutIcon />
            </ListItemIcon>
            {isOpen && <ListItemText primary="Logout"
                                     primaryTypographyProps={{ fontSize: "0.875rem", fontWeight: 600 }} />}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile && !isOpen && (
        <Fab size="medium" onClick={handleToggle} aria-label="Open sidebar"
             sx={{ position: "fixed", top: 16, left: 16, zIndex: 1300, bgcolor: BRAND_PRIMARY,
                   color: "#fff", "&:hover": { bgcolor: "#115e59" } }}>
          <MenuIcon />
        </Fab>
      )}

      {!isMobile && (
        <Drawer variant="permanent" open={isOpen}
          PaperProps={{ sx: { width: currentWidth, transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
                              overflowX: "hidden", border: "none", boxShadow: "0 0 20px rgba(0,0,0,0.04)",
                              boxSizing: "border-box" } }}
          sx={{ width: currentWidth, flexShrink: 0,
                transition: "width 0.25s cubic-bezier(0.4,0,0.2,1)",
                "& .MuiDrawer-paper": { width: currentWidth, boxSizing: "border-box" } }}>
          {drawerContent}
        </Drawer>
      )}

      {isMobile && (
        <Drawer variant="temporary" open={isOpen} onClose={() => onSidebarToggle(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{ sx: { width: SIDEBAR_WIDTH_EXPANDED, border: "none" } }}>
          {drawerContent}
        </Drawer>
      )}

      {showLogoutModal && (
        <LogoutConfirmation onClose={() => setShowLogoutModal(false)}
                            onConfirm={handleLogoutConfirm} loading={logoutLoading} />
      )}
    </>
  );
};

export default TechnicianSidebar;